import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ProductCard from "../components/ProductCard";
import SilverHero from "../components/SilverHero";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { Loader2 } from "lucide-react";

const PURITIES = ["18k", "22k"];
const COLLECTIONS = ["Daily Wear", "Festive", "Bridal"];
// 12 is tuned for a 2-col mobile grid (6 rows above-the-fold) — gives a snappy
// first paint. Subsequent pages fetch 24 at a time via infinite-scroll.
const FIRST_PAGE_SIZE = 12;
const NEXT_PAGE_SIZE = 24;

const SORT_OPTIONS = [
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "featured", label: "Featured picks" },
  { value: "newest", label: "Newest first" },
];

export default function Products() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the single source of truth for the shared filters (collection, price
  // range, sort, search). This stops any "URL vs local state" race where a stale
  // URL value re-overrides a local change made in-between (iter14 bug). `purity`
  // stays purely local because it's not currently sync'd through the URL.
  const PRICE_MIN = 0;
  const PRICE_MAX = 500000;
  const collection = searchParams.get("collection") || "";
  const priceRange = [
    Number(searchParams.get("min_price")) || PRICE_MIN,
    Number(searchParams.get("max_price")) || PRICE_MAX,
  ];
  const sort = searchParams.get("sort") || "featured";
  const searchTerm = (searchParams.get("search") || "").trim();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [purity, setPurity] = useState([]);

  const isSilver = category === "silver";

  // Patch helper — writes a filter to the URL (replace), omitting the key when
  // it's at the default. Keeps the URL clean + hash-shareable.
  const patchParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === "" ||
          (k === "sort" && v === "featured") ||
          (k === "min_price" && Number(v) <= PRICE_MIN) ||
          (k === "max_price" && Number(v) >= PRICE_MAX)) {
        next.delete(k);
      } else {
        next.set(k, String(v));
      }
    }
    setSearchParams(next, { replace: true });
  };

  const setCollection = (val) => patchParams({ collection: val });
  const setPriceRange = ([lo, hi]) => patchParams({ min_price: lo, max_price: hi });

  // Build the query params once so first-page + load-more share the exact same filter.
  // We only send min_price/max_price when the user has actively narrowed the slider
  // from its defaults, and only send `sort` when it's not the default "featured".
  // This lets the backend take its DB-side fast-path on the common first-page load.
  const buildParams = (offset, pageSize) => {
    const params = { limit: pageSize, offset };
    if (category) params.category = category;
    if (collection) params.collection = collection;
    if (purity.length === 1) params.purity = purity[0];
    if (searchTerm) params.search = searchTerm;
    if (priceRange[0] > PRICE_MIN) params.min_price = priceRange[0];
    if (priceRange[1] < PRICE_MAX) params.max_price = priceRange[1];
    if (sort && sort !== "featured") params.sort = sort;
    return params;
  };

  // Guard against stale responses: when filters change quickly, only the newest
  // in-flight request may update state. A ref holds the latest request id.
  const requestIdRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    const myId = ++requestIdRef.current;
    axios.get(`${API}/products`, { params: buildParams(0, FIRST_PAGE_SIZE) })
      .then(r => {
        if (myId !== requestIdRef.current) return;  // a newer request has fired
        const items = r.data?.items || [];
        setProducts(items);
        setTotal(r.data?.total ?? items.length);
        setHasMore(!!r.data?.has_more);
      })
      .catch(err => {
        if (myId !== requestIdRef.current) return;
        console.warn("Products fetch failed:", err);
        setProducts([]);
        setTotal(0);
        setHasMore(false);
      })
      .finally(() => {
        if (myId === requestIdRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, purity, collection, priceRange[0], priceRange[1], sort, searchTerm]);

  const loadingMoreRef = useRef(false);
  const loadMore = async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const myId = requestIdRef.current;
    try {
      const r = await axios.get(`${API}/products`, { params: buildParams(products.length, NEXT_PAGE_SIZE) });
      // Drop the response if the filter changed while this page was fetching —
      // otherwise we'd append stale items on top of a fresh list.
      if (myId !== requestIdRef.current) return;
      const items = r.data?.items || [];
      setProducts(prev => [...prev, ...items]);
      setHasMore(!!r.data?.has_more);
    } catch (err) {
      // Network blip — keep hasMore=true so the user can retry via the button.
      console.warn("Load more failed:", err);
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  };

  // Infinite-scroll sentinel — IntersectionObserver fires loadMore when the user
  // scrolls near the end of the current list. Falls back to the manual button
  // if JS observers are unavailable (very old browsers).
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "600px 0px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, products.length]);

  const togglePurity = (p) => {
    setPurity(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const changeSort = (value) => patchParams({ sort: value });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="products-page">
      {isSilver && <SilverHero />}

      {!isSilver && (
        <div className="mb-10">
          <p className="label-small text-[#C5A059] mb-2">Sumanya · {searchTerm ? "Search" : "Collections"}</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2C2A29] capitalize" data-testid="products-title">
            {searchTerm ? `Results for "${searchTerm}"` : (category || collection || "All Jewellery")}
          </h1>
          <p className="text-sm text-[#8A8480] mt-2">{total} piece{total === 1 ? "" : "s"} curated for you</p>
        </div>
      )}

      {isSilver && (
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl text-[#2C2A29]" data-testid="products-title">Shop the Silver Edit</h2>
          <p className="text-xs text-[#8A8480]" data-testid="silver-count">{total} piece{total === 1 ? "" : "s"}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        {/* FILTERS */}
        <aside className="space-y-8" data-testid="filters-sidebar">
          {!isSilver && (
            <div>
              <h3 className="label-small text-[#8A8480] mb-4">Purity</h3>
              <div className="space-y-3">
                {PURITIES.map(p => (
                  <label key={p} className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={purity.includes(p)}
                      onCheckedChange={() => togglePurity(p)}
                      data-testid={`filter-purity-${p}`}
                    />
                    <span className="text-sm">{p} Gold</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {isSilver && (
            <div className="bg-[#FDFBF7] border border-[#EAE3D9] rounded-2xl p-4" data-testid="silver-purity-note">
              <div className="label-small text-[#8A8480] mb-1">Purity</div>
              <div className="text-sm font-semibold text-[#2C2A29]">925 Sterling Silver</div>
              <div className="text-[11px] text-[#8A8480] mt-1">92.5% pure sterling silver</div>
            </div>
          )}

          <div>
            <h3 className="label-small text-[#8A8480] mb-4">Collection</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={collection === ""}
                  onCheckedChange={() => setCollection("")}
                  data-testid="filter-collection-all"
                />
                <span className="text-sm">All</span>
              </label>
              {COLLECTIONS.map(c => (
                <label key={c} className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={collection === c}
                    onCheckedChange={() => setCollection(collection === c ? "" : c)}
                    data-testid={`filter-collection-${c.replace(/\s+/g, "-")}`}
                  />
                  <span className="text-sm">{c}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="label-small text-[#8A8480] mb-4">Price</h3>
            <Slider
              defaultValue={priceRange}
              key={`${priceRange[0]}-${priceRange[1]}`}
              onValueCommit={setPriceRange}
              min={0}
              max={500000}
              step={5000}
              data-testid="filter-price-slider"
            />
            <div className="flex justify-between text-xs text-[#595552] mt-3">
              <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
              <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
            </div>
          </div>
        </aside>

        {/* GRID */}
        <div>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3" data-testid="products-sort-row">
            <div className="text-xs text-[#8A8480]">
              {loading ? "Loading pieces…" : `Showing ${products.length} of ${total} piece${total === 1 ? "" : "s"}`}
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="label-small text-[#8A8480]">Sort by</label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => changeSort(e.target.value)}
                className="text-sm bg-white border border-[#EAE3D9] rounded-full px-4 py-2 hover:border-[#C5A059] focus:outline-none focus:border-[#C5A059] cursor-pointer"
                data-testid="products-sort-select"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="products-loading-skeleton">
              {Array.from({ length: FIRST_PAGE_SIZE }).map((_, i) => (
                <div key={i} className="aspect-square skeleton-shimmer rounded-2xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20" data-testid="no-products">
              <p className="font-display text-3xl text-[#2C2A29]">No pieces found</p>
              <p className="text-[#8A8480] mt-2 text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="products-grid">
                {products.map(p => <ProductCard key={p.product_id} p={p} />)}
              </div>
              {/* Infinite-scroll sentinel + fallback manual Load more button */}
              {hasMore && (
                <>
                  <div ref={sentinelRef} className="h-10" aria-hidden="true" />
                  <div className="flex justify-center mt-6">
                    <Button
                      onClick={loadMore}
                      disabled={loadingMore}
                      variant="outline"
                      className="rounded-full border-[#C5A059] text-[#8B1E28] hover:bg-[#FFF5F3] px-8 py-6"
                      data-testid="products-load-more"
                    >
                      {loadingMore ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading more…</>
                      ) : (
                        <>Load more · {total - products.length} more</>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
